import { useEffect, useRef, useState, useMemo } from "react";
import { MapPin, Loader2, Crosshair, X, Share2 } from "lucide-react";
import { loadMaps, reverseGeocode } from "@/lib/geo";
import api from "@/lib/api";
import { toast } from "sonner";

// Draggable-pin map picker for a precise pickup/drop when the typed address isn't exact.
// Opens as a modal; drag the pin (or tap the map) → reverse-geocodes to an address + coords.
// When the opposite point + a mobility rate are known, shows a live distance + est. fare.
export function MapPinPicker({ open, initial = null, title = "Set location on map", travelType = null, otherPoint = null, otherLabel = "", onConfirm, onClose, testid = "map-pin-picker" }) {
  const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const mapRef = useRef(null);
  const mapObj = useRef(null);
  const marker = useRef(null);
  const tmr = useRef(null);
  const [ready, setReady] = useState(false);
  const [addr, setAddr] = useState(initial?.address || "");
  const [pos, setPos] = useState(initial?.lat ? { lat: initial.lat, lng: initial.lng } : null);
  const [geocoding, setGeocoding] = useState(false);
  const [rate, setRate] = useState(null);

  useEffect(() => {
    if (!open || !travelType) { setRate(null); return; }
    api.get(`/marketplace/mobility-rate?travel_type=${encodeURIComponent(travelType)}`).then((r) => setRate(r.data)).catch(() => setRate(null));
  }, [open, travelType]);

  const fare = useMemo(() => {
    if (!pos || !otherPoint || otherPoint.lat == null) return null;
    const R = 6371, toR = (d) => (d * Math.PI) / 180;
    const dLat = toR(otherPoint.lat - pos.lat), dLng = toR(otherPoint.lng - pos.lng);
    const s = Math.sin(dLat / 2) ** 2 + Math.cos(toR(pos.lat)) * Math.cos(toR(otherPoint.lat)) * Math.sin(dLng / 2) ** 2;
    const kmRaw = 2 * R * Math.asin(Math.sqrt(s));
    const km = Math.round(kmRaw * 10) / 10;
    if (!(rate && rate.count && rate.median_rate)) return { km, hasRate: false };
    const minKm = rate.min_km || 0;
    const billedRaw = Math.max(kmRaw, minKm);
    const billed = Math.round(billedRaw * 10) / 10;
    const perKm = rate.median_rate;
    const lo = Math.round(billedRaw * rate.min_rate);
    const hi = Math.round(billedRaw * rate.max_rate);
    return { km, hasRate: true, minKm, billed, perKm, lo, hi, total: Math.round(billedRaw * perKm), minApplies: kmRaw < minKm };
  }, [pos, otherPoint, rate]);

  const settle = (lat, lng) => {
    setPos({ lat, lng });
    if (tmr.current) clearTimeout(tmr.current);
    setGeocoding(true);
    tmr.current = setTimeout(async () => {
      const a = await reverseGeocode(lat, lng);
      if (a) setAddr(a);
      setGeocoding(false);
    }, 400);
  };

  useEffect(() => {
    if (!open || !key) return;
    let live = true;
    (async () => {
      await loadMaps(key);
      if (!live || !mapRef.current) return;
      const g = window.google;
      const start = pos || { lat: 20.5937, lng: 78.9629 }; // India centroid fallback
      const map = new g.maps.Map(mapRef.current, {
        center: start, zoom: pos ? 16 : 5, disableDefaultUI: true, zoomControl: true, gestureHandling: "greedy",
      });
      const mk = new g.maps.Marker({ position: start, map, draggable: true, animation: g.maps.Animation.DROP });
      mk.addListener("dragend", (e) => settle(e.latLng.lat(), e.latLng.lng()));
      map.addListener("click", (e) => { mk.setPosition(e.latLng); settle(e.latLng.lat(), e.latLng.lng()); });
      mapObj.current = map; marker.current = mk; setReady(true);
      if (!pos) locateMe(map, mk);
    })();
    return () => { live = false; };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const locateMe = (map, mk) => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (p) => {
        const lat = p.coords.latitude, lng = p.coords.longitude;
        (map || mapObj.current)?.setCenter({ lat, lng });
        (map || mapObj.current)?.setZoom(16);
        (mk || marker.current)?.setPosition({ lat, lng });
        settle(lat, lng);
      },
      () => {}, { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  if (!open) return null;

  const isDrop = title.toLowerCase().includes("drop");
  const doShare = async () => {
    if (!pos) { toast.error("Move the pin to a spot first."); return; }
    const gmap = `https://www.google.com/maps/search/?api=1&query=${pos.lat},${pos.lng}`;
    const thisLbl = isDrop ? "Drop" : "Pickup";
    let text = `${thisLbl}: ${addr || `${pos.lat.toFixed(5)}, ${pos.lng.toFixed(5)}`}`;
    if (otherLabel) text += `\n${isDrop ? "Pickup" : "Drop"}: ${otherLabel}`;
    if (fare) text += fare.hasRate ? `\n≈ ${fare.km} km · est. ₹${fare.lo}${fare.hi > fare.lo ? `–₹${fare.hi}` : ""}` : `\n≈ ${fare.km} km`;
    text += `\n${gmap}`;
    try {
      if (navigator.share) { await navigator.share({ title: "Ride location", text }); }
      else { await navigator.clipboard.writeText(text); toast.success("Copied — paste it to your driver or friend"); }
    } catch (e) { /* user dismissed the share sheet */ }
  };
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4" data-testid={testid} onClick={onClose}>
      <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <p className="flex items-center gap-2 text-sm font-bold text-slate-900"><MapPin className="h-4 w-4 text-sky-600" /> {title}</p>
          <button type="button" onClick={onClose} data-testid={`${testid}-close`} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><X className="h-4 w-4" /></button>
        </div>
        {!key ? (
          <div className="p-6 text-center text-sm text-slate-500" data-testid={`${testid}-nokey`}>Map picker needs Google Maps to be configured. Please type the address instead.</div>
        ) : (
          <>
            <div className="relative">
              <div ref={mapRef} className="h-72 w-full bg-slate-100" data-testid={`${testid}-map`} />
              {!ready && <div className="absolute inset-0 flex items-center justify-center bg-slate-50/80"><Loader2 className="h-6 w-6 animate-spin text-sky-500" /></div>}
              <button type="button" onClick={() => locateMe()} data-testid={`${testid}-locate`} className="absolute bottom-3 right-3 inline-flex items-center gap-1 rounded-full bg-white px-3 py-1.5 text-[11px] font-semibold text-sky-700 shadow-md hover:bg-sky-50"><Crosshair className="h-3.5 w-3.5" /> My location</button>
            </div>
            <div className="p-4">
              <p className="mb-1 text-[11px] font-semibold text-slate-500">Drag the pin or tap the map to set the exact point</p>
              <div className="flex items-center gap-2 rounded-lg border border-slate-200 px-2 py-2">
                {geocoding ? <Loader2 className="h-4 w-4 shrink-0 animate-spin text-slate-400" /> : <MapPin className="h-4 w-4 shrink-0 text-slate-400" />}
                <span className="min-w-0 flex-1 truncate text-sm text-slate-700" data-testid={`${testid}-address`}>{addr || (geocoding ? "Finding address…" : "Move the pin to pick a spot")}</span>
              </div>
              {fare && (
                <div className="mt-2" data-testid={`${testid}-fare`}>
                  <p className="flex items-center gap-1 text-xs font-bold text-emerald-700">
                    ≈ {fare.km} km{fare.hasRate ? ` · est. ₹${fare.lo}${fare.hi > fare.lo ? `–₹${fare.hi}` : ""}` : " to the other point"}
                  </p>
                  {fare.hasRate && (
                    <div className="mt-1.5 space-y-0.5 rounded-lg bg-slate-50 px-2.5 py-2 text-[11px] text-slate-500" data-testid={`${testid}-fare-breakdown`}>
                      <div className="flex justify-between"><span>Base fare</span><span>{fare.minApplies ? `min ${fare.minKm} km applies` : "—"}</span></div>
                      <div className="flex justify-between"><span>Distance ({fare.billed} km billed)</span><span>× ₹{fare.perKm}/km</span></div>
                      <div className="flex justify-between"><span>Surge</span><span>none (×1.0)</span></div>
                      <div className="mt-0.5 flex justify-between border-t border-slate-200 pt-1 font-semibold text-slate-700"><span>Estimated fare</span><span>≈ ₹{fare.total}</span></div>
                      <p className="pt-0.5 text-[10px] text-slate-400">Indicative from live posted per-km rates — final fare confirmed by the provider.</p>
                    </div>
                  )}
                </div>
              )}
              <div className="mt-3 flex items-center justify-between gap-2">
                <button type="button" onClick={doShare} disabled={!pos} data-testid={`${testid}-share`} className="inline-flex items-center gap-1.5 rounded-full border border-sky-200 px-3 py-2 text-sm font-semibold text-sky-600 hover:bg-sky-50 disabled:opacity-50"><Share2 className="h-4 w-4" /> Share</button>
                <div className="flex gap-2">
                  <button type="button" onClick={onClose} data-testid={`${testid}-cancel`} className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
                  <button type="button" disabled={!pos || geocoding} data-testid={`${testid}-confirm`}
                    onClick={() => { if (!pos) { toast.error("Move the pin to a spot first."); return; } onConfirm && onConfirm({ address: addr, lat: pos.lat, lng: pos.lng }); }}
                    className="rounded-full bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-50">Use this location</button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default MapPinPicker;
